import React, { memo } from 'react'
import { Col, Button, Table, Empty, Input, Space, theme, Skeleton, Card, Row, Tooltip } from 'antd'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faRefresh, faSearch, faPlus } from '@fortawesome/free-solid-svg-icons'
import Templates from '../utils/templates'
import { sortBy } from 'lodash'
import useClinicsList from '../hooks/useClinicsList'
import PropTypes from 'prop-types'

// Add spin animation with more refined keyframes
const spinAnimation = `
  @keyframes spin {
    0% { transform: rotate(0deg); }
    50% { transform: rotate(180deg); }
    100% { transform: rotate(360deg); }
  }
  
  .ant-table-row:hover .service-icon {
    transform: scale(1.1);
    transition: all 0.2s ease;
  }
  
  .service-icon {
    transition: all 0.2s ease;
  }
  
  .ant-table-row {
    transition: all 0.3s ease;
  }
  
  .ant-table-row:hover {
    background-color: rgba(24, 144, 255, 0.05) !important;
  }
`

const _ListView = ({ data, refreshView, createRecord, editRecord, loading, handleSearch }) => {
  const { filters, handleCreateClick } = useClinicsList({ data, createRecord })

  const tableColumns = Templates.columnTemplate(editRecord, filters)

  const { token } = theme.useToken()

  // Function to generate skeleton rows - optimized
  const generateSkeletonRows = () => {
    return Array(5)
      .fill()
      .map((_, i) => ({
        key: `skeleton-${i}`,
        _id: `skeleton-${i}`,
        skeleton: true
      }))
  }

  // Function to generate skeleton columns - optimized
  const skeletonColumns = () => {
    // Get columns and filter out the action column (it's the first one)
    let columns = tableColumns
      .filter((col) => col.key !== 'action')
      .map((col) => ({
        ...col,
        render: () => <Skeleton.Input active size='small' style={{ width: '100%' }} />,
        onCell: () => ({
          style: { cursor: 'default' }
        })
      }))

    // Add actions column at the end (fixed to right)
    columns.push({
      title: <h2>Actions</h2>,
      key: 'actions',
      align: 'center',
      fixed: 'right',
      width: 100,
      render: () => <Skeleton.Input active size='small' style={{ width: '100%' }} />
    })

    return columns
  }

  return (
    <>
      <style>{spinAnimation}</style>
      <Card bordered={false} className='clinic-list-card' bodyStyle={{ padding: '16px 24px' }}>
        <div style={{ marginBottom: '12px' }}>
          <Row gutter={[0, 0]} align='middle' justify='space-between' wrap>
            <Col xs={24} sm={14} md={16} lg={16} xl={16}>
              <Input
                placeholder='Search for Facility Name or Address'
                prefix={
                  <div
                    style={{
                      background: token.actionColor,
                      borderRadius: '50%',
                      width: '24px',
                      height: '24px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      marginRight: '8px'
                    }}
                  >
                    <FontAwesomeIcon icon={faSearch} style={{ color: 'white', fontSize: '12px' }} />
                  </div>
                }
                allowClear
                onChange={(e) => handleSearch(e.target.value)}
                style={{
                  width: '100%',
                  maxWidth: '500px',
                  borderRadius: '6px',
                  height: '40px'
                }}
                disabled={loading}
                size='middle'
              />
            </Col>
            <Col xs={24} sm={10} md={8} lg={8} xl={8}>
              <Row gutter={[12, 8]} justify='end'>
                <Col>
                  <Tooltip title='Refresh facility list'>
                    <Button
                      icon={
                        <FontAwesomeIcon
                          icon={faRefresh}
                          style={{
                            color: token.actionColor,
                            animation: loading ? 'spin 1s linear infinite' : 'none',
                            marginRight: 8
                          }}
                        />
                      }
                      onClick={() => {
                        refreshView()
                      }}
                      disabled={loading}
                      type='default'
                      size='large'
                      style={{
                        boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        height: '40px',
                        borderRadius: '6px',
                        background: 'rgba(24, 144, 255, 0.1)',
                        borderColor: 'rgba(24, 144, 255, 0.3)',
                        color: token.actionColor,
                        fontWeight: '500'
                      }}
                    >
                      Refresh
                    </Button>
                  </Tooltip>
                </Col>
                <Col>
                  <Button
                    type='primary'
                    style={{
                      background: loading ? undefined : token.actionColor,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '8px',
                      height: '40px',
                      boxShadow: '0 2px 6px rgba(0,0,0,0.1)',
                      borderRadius: '6px'
                    }}
                    onClick={handleCreateClick}
                    disabled={loading}
                    size='large'
                    id='clinic_add_btn'
                    icon={<FontAwesomeIcon icon={faPlus} />}
                  >
                    Add Facility
                  </Button>
                </Col>
              </Row>
            </Col>
          </Row>
        </div>

        <Table
          bordered
          size='small'
          id='clinic_list_view'
          loading={false} // We handle loading state ourselves
          rowKey='_id'
          dataSource={loading ? generateSkeletonRows() : sortBy(data, 'name')}
          pagination={{
            position: ['bottomRight'],
            showSizeChanger: true,
            pageSizeOptions: ['10', '20', '50', '100'],
            showTotal: (total) => `Total ${total} facilities`,
            size: 'default'
          }}
          scroll={{ x: 950 }}
          columns={
            loading
              ? skeletonColumns()
              : [
                  ...tableColumns.filter((col) => col.key !== 'action'), // Filter out the left-side action column
                  {
                    title: <h2>Actions</h2>,
                    key: 'actions',
                    align: 'center',
                    fixed: 'right',
                    width: 120,
                    render: (_, record) => (
                      <Space size={8}>
                        <Tooltip title='Edit facility details'>
                          <Button
                            type='primary'
                            size='small'
                            onClick={(e) => {
                              e.stopPropagation() // Prevent row click event
                              editRecord(record)
                            }}
                            style={{
                              borderRadius: '6px',
                              fontWeight: '500',
                              fontSize: '12px'
                            }}
                          >
                            Edit
                          </Button>
                        </Tooltip>
                        {/* Temporarily disabled delete functionality */}
                        {/* <Popconfirm
                          title={<span style={{ color: token.colorWarning }}>Delete Facility</span>}
                          description='Are you sure you want to delete this facility? This action cannot be undone.'
                          onConfirm={() => deleteRecord(record)}
                          okText='Yes, Delete'
                          cancelText='Cancel'
                          okButtonProps={{ 
                            danger: true,
                            style: { fontWeight: '500' }
                          }}
                          cancelButtonProps={{
                            style: { fontWeight: '500' }
                          }}
                        >
                          <Tooltip title='Delete facility'>
                            <Button
                              danger
                              size='small'
                              onClick={(e) => {
                                e.stopPropagation() // Prevent row click event
                              }}
                              style={{
                                borderRadius: '6px',
                                fontWeight: '500',
                                fontSize: '12px'
                              }}
                            >
                              Delete
                            </Button>
                          </Tooltip>
                        </Popconfirm> */}
                      </Space>
                    )
                  }
                ]
          }
          onRow={(record) => ({
            style: {
              cursor: 'default',
              transition: 'background-color 0.3s ease'
            }
          })}
          locale={{
            emptyText: loading ? (
              <div style={{ padding: '40px 0' }}>
                <Skeleton active paragraph={{ rows: 3 }} />
              </div>
            ) : (
              <Empty
                description='There are currently no registered facilities to show.'
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                style={{ padding: '40px 0' }}
              />
            )
          }}
          rowClassName={(record) => (record.skeleton ? 'skeleton-row' : '')}
        />
      </Card>
    </>
  )
}

_ListView.propTypes = {
  data: PropTypes.array.isRequired,
  refreshView: PropTypes.func.isRequired,
  createRecord: PropTypes.func.isRequired,
  editRecord: PropTypes.func.isRequired,
  loading: PropTypes.bool.isRequired,
  handleSearch: PropTypes.func.isRequired,
  deleteRecord: PropTypes.func.isRequired
}

const ListView = memo(_ListView)

export default ListView
